import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { IReqLeadCreation, ITradeInState } from 'pages/tradeIn/TradeInInterface';
import { Lead } from 'pages/leadGen/LeadGenInterface';
import { handleApiError } from './CommonModuleState';
import {
  BlackbookVehicleColorsByUVC,
  BlackbookVehicleOptions,
  ProveVerificationComplete,
  ProveVerificationStart,
  ProveVerificationValidate,
  PublicLeadCreationUrl,
  VehicleLookupAutocomplete,
} from './ApiEndPoints';

const initialState: ITradeInState = {
  stepCount: 0,
  loading: false,
  vehicleAutoCompleteData: [],
  leadCreationData: {},
  updateTradeInData: {},
  sendOtpData: {},
  verifyOtpData: {},
  vehicleColorData: [],
  publicLeadCreationData: {},
  cbbData: {},
  vehicleOptions: [],
  proveData: {},
  proveLoading: false,
  proveVerificationData: {},
};

export const postTradeInLeadCreation = createAsyncThunk(
  'tradeIn/leadCreation',
  async (data: { data: IReqLeadCreation | Lead; leadTypeID: string }) => {
    try {
      const response = await axios.post(`${PublicLeadCreationUrl}${data.leadTypeID}`, data.data);

      return response.data;
    } catch (error: unknown) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  }
);

export const vehicleLookupAutocomplete = createAsyncThunk(
  'tradeIn/vehicleLookupAutocomplete',
  async (searchText: string) => {
    try {
      const response = await axios.get(VehicleLookupAutocomplete, {
        params: {
          searchText,
        },
      });
      return response.data;
    } catch (e) {
      console.log(e);
    }
  }
);

export const blackbookVehicleOptions = createAsyncThunk(
  'tradeIn/blackbookVehicleOptions',
  async (requestParams: { UVC: string }) => {
    try {
      const response = await axios.get(BlackbookVehicleOptions, {
        params: requestParams,
      });
      return response.data;
    } catch (e) {
      console.log(e);
    }
  }
);

export const vehicleColorsLookup = createAsyncThunk(
  'tradeIn/vehicleColorsLookup',
  async (requestParams: { UVC: string }) => {
    try {
      const response = await axios.get(BlackbookVehicleColorsByUVC, {
        params: requestParams,
      });

      return response.data;
    } catch (e) {
      console.log(e);
    }
  }
);

export const getCBBRate = createAsyncThunk(
  'tradeIn/getCBBRate',
  async (data: { data: IReqLeadCreation | Lead; leadTypeID: string }) => {
    try {
      const response = await axios.post(`${PublicLeadCreationUrl}${data.leadTypeID}`, data.data);

      return response.data;
    } catch (error: unknown) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  }
);

export const proveVerificationStart = createAsyncThunk(
  'tradeIn/proveVerificationStart',
  async (reqData: { data: { leadId: string; flowType: string; phoneNumber: string }; locale: string }) => {
    try {
      const response = await axios.post(ProveVerificationStart, reqData?.data);

      return response.data;
    } catch (error: unknown) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  }
);

export const proveVerificationValidate = createAsyncThunk(
  'tradeIn/proveVerificationValidate',
  async (reqData: { data: { correlationId: string } }) => {
    try {
      const response = await axios.post(ProveVerificationValidate, reqData?.data);

      return response.data;
    } catch (error: unknown) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  }
);

export const proveVerificationComplete = createAsyncThunk(
  'tradeIn/proveVerificationComplete',
  async (reqData: { data: { leadId: string; correlationId: string } }) => {
    try {
      const response = await axios.post(ProveVerificationComplete, reqData?.data);

      return response.data;
    } catch (error: unknown) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  }
);

export const tradeInState = createSlice({
  name: 'tradeIn',
  initialState,
  reducers: {
    stepIncrement: (state) => {
      state.stepCount += 1;
    },
    resetStepCount: (state) => {
      state.stepCount = 1;
    },
    updateTradeInData: (state, action) => {
      state.updateTradeInData = {
        ...state.updateTradeInData,
        ...action.payload,
      };
    },
    clearVehicleAutoCompleteData: (state) => {
      return {
        ...state,
        vehicleAutoCompleteData: initialState.vehicleAutoCompleteData,
      };
    },

    clearLeadCreation: (state) => {
      return {
        ...state,
        leadCreationData: initialState.leadCreationData,
      };
    },
    clearProveData: (state) => {
      return {
        ...state,
        proveData: {},
        proveLoading: false,
        proveVerificationData: {},
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(postTradeInLeadCreation.pending, (state) => {
        state.loading = true;
      })
      .addCase(postTradeInLeadCreation.fulfilled, (state, action) => {
        state.loading = false;
        state.leadCreationData = action.payload;
      })
      .addCase(postTradeInLeadCreation.rejected, (state, action) => {
        state.loading = false;
        state.leadCreationData = {
          message: action.error.message,
          success: false,
        };
      })
      .addCase(vehicleLookupAutocomplete.fulfilled, (state, action) => {
        state.loading = false;
        state.vehicleAutoCompleteData = action.payload?.message?.autocomplete;
      })
      .addCase(vehicleColorsLookup.fulfilled, (state, action) => {
        state.loading = false;
        state.vehicleColorData = action.payload?.message?.vehicle_colors?.category_list;
      })
      .addCase(getCBBRate.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCBBRate.fulfilled, (state, action) => {
        state.loading = false;
        state.cbbData = action.payload;
      })
      .addCase(blackbookVehicleOptions.fulfilled, (state, action) => {
        state.loading = false;
        state.vehicleOptions = action.payload?.message?.addDeductList;
      })
      .addCase(blackbookVehicleOptions.rejected, (state) => {
        state.loading = false;
        state.vehicleOptions = [];
      })
      .addCase(proveVerificationStart.pending, (state) => {
        state.proveLoading = true;
      })
      .addCase(proveVerificationStart.fulfilled, (state, action) => {
        state.proveLoading = false;
        state.proveData = action.payload;
      })
      .addCase(proveVerificationStart.rejected, (state) => {
        state.proveLoading = false;
      })
      .addCase(proveVerificationComplete.pending, (state) => {
        state.proveLoading = true;
      })
      .addCase(proveVerificationComplete.fulfilled, (state, action) => {
        state.proveLoading = false;
        state.proveVerificationData = action.payload;
      })
      .addCase(proveVerificationComplete.rejected, (state) => {
        state.proveLoading = false;
      });
  },
});

// Action creators are generated for each case reducer function
export const {
  stepIncrement,
  resetStepCount,
  clearVehicleAutoCompleteData,
  updateTradeInData,
  clearLeadCreation,
  clearProveData,
} = tradeInState.actions;
export default tradeInState.reducer;
